import { ActivityIcon, Clock3Icon, Settings2Icon, UserRoundIcon } from "lucide-react";
import { useState } from "react";
import type {
  CmmdBoardTask,
  CmmdProjectAgentInstance,
  CmmdProjectAgentMember,
  CmmdProjectLeadEvents,
} from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { AgentEventHistory } from "./AgentEventHistory";
import { AgentMemberAvatarPicker } from "./AgentMemberAvatarPicker";
import {
  canRenameAgentMember,
  isProviderLockedAgentKind,
  providerLockedDisplayName,
} from "./agentMemberDisplayName";
import { formatTeamRole } from "./projectTeamLabels";

type InspectorTab = "overview" | "activity" | "settings";

interface AgentMemberInspectorBodyProps {
  readonly member: CmmdProjectAgentMember;
  readonly tasks: readonly CmmdBoardTask[];
  readonly instances: readonly CmmdProjectAgentInstance[];
  readonly historyLoading: boolean;
  readonly historyError: string | null;
  readonly events: CmmdProjectLeadEvents["events"];
  readonly eventsLoading: boolean;
  readonly eventsError: string | null;
  readonly saving: boolean;
  readonly error: string | null;
  readonly name: string;
  readonly onNameChange: (name: string) => void;
  readonly onRename: () => void;
  readonly onRetire: () => void;
  readonly onUpdateAvatar: (
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
}

const tabs: ReadonlyArray<{
  readonly id: InspectorTab;
  readonly label: string;
  readonly icon: typeof UserRoundIcon;
}> = [
  { id: "overview", label: "Overview", icon: UserRoundIcon },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "settings", label: "Settings", icon: Settings2Icon },
];

export function AgentMemberInspectorBody(props: AgentMemberInspectorBodyProps) {
  const [activeTab, setActiveTab] = useState<InspectorTab>("overview");
  return (
    <div>
      <div
        role="tablist"
        aria-label="Agent details"
        className="sticky top-0 z-10 -mx-5 flex border-b border-border bg-popover px-5"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              id={`agent-inspector-tab-${tab.id}`}
              role="tab"
              aria-controls="agent-inspector-panel"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={`relative flex h-11 items-center gap-2 px-3 text-sm transition-colors duration-150 motion-reduce:transition-none ${selected ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => {
                const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
                if (offset === 0) return;
                event.preventDefault();
                const currentIndex = tabs.findIndex(({ id }) => id === activeTab);
                const nextTab =
                  tabs[(currentIndex + offset + tabs.length) % tabs.length] ?? tabs[0]!;
                setActiveTab(nextTab.id);
                document.getElementById(`agent-inspector-tab-${nextTab.id}`)?.focus();
              }}
            >
              <Icon className="size-3.5" />
              {tab.label}
              <span
                aria-hidden
                className={`absolute inset-x-3 bottom-0 h-px origin-center bg-primary transition-[opacity,scale] duration-200 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none ${selected ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"}`}
              />
            </button>
          );
        })}
      </div>
      <div
        id="agent-inspector-panel"
        role="tabpanel"
        aria-labelledby={`agent-inspector-tab-${activeTab}`}
        className="py-5"
      >
        {activeTab === "overview" ? <OverviewTab {...props} /> : null}
        {activeTab === "activity" ? (
          <AgentEventHistory
            events={props.events}
            loading={props.eventsLoading}
            error={props.eventsError}
            framed={false}
          />
        ) : null}
        {activeTab === "settings" ? <SettingsTab {...props} /> : null}
      </div>
    </div>
  );
}

function OverviewTab(props: AgentMemberInspectorBodyProps) {
  return (
    <div className="space-y-6">
      <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
        <dt className="text-muted-foreground">Role</dt>
        <dd>{formatTeamRole(props.member.primaryRole)}</dd>
        <dt className="text-muted-foreground">Status</dt>
        <dd className="flex items-center gap-2 capitalize">
          <span className="size-1.5 rounded-full bg-success" />
          {props.member.lifecycleStatus}
        </dd>
        <dt className="text-muted-foreground">Capabilities</dt>
        <dd className="leading-5 text-muted-foreground">
          {props.member.capabilities.length > 0
            ? props.member.capabilities.join(", ")
            : "None declared"}
        </dd>
      </dl>
      <div className="grid gap-6 border-t border-border pt-5 sm:grid-cols-2">
        <WorkList tasks={props.tasks} />
        <InstanceList
          instances={props.instances}
          loading={props.historyLoading}
          error={props.historyError}
        />
      </div>
    </div>
  );
}

function WorkList({ tasks }: { readonly tasks: readonly CmmdBoardTask[] }) {
  return (
    <section>
      <h3 className="text-sm font-medium">Current work</h3>
      {tasks.length > 0 ? (
        <ul className="mt-3 divide-y divide-border border-y border-border">
          {tasks.map((task) => (
            <li key={task.id} className="py-3 text-sm">
              <span className="block font-medium leading-5">{task.title}</span>
              <span className="mt-1 block text-xs capitalize text-muted-foreground">
                {task.status.replaceAll("_", " ")}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">Nothing assigned.</p>
      )}
    </section>
  );
}

function InstanceList(props: {
  readonly instances: readonly CmmdProjectAgentInstance[];
  readonly loading: boolean;
  readonly error: string | null;
}) {
  return (
    <section>
      <h3 className="text-sm font-medium">Recent sessions</h3>
      {props.loading ? (
        <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
      ) : props.error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          Sessions unavailable: {props.error}
        </p>
      ) : props.instances.length > 0 ? (
        <ul className="mt-3 divide-y divide-border border-y border-border">
          {props.instances.map((instance) => (
            <li key={instance.id} className="py-3 text-sm">
              <span className="flex items-center gap-2 font-medium">
                <Clock3Icon className="size-3.5 text-muted-foreground" />
                {instance.clientName ?? instance.sourceKind}
              </span>
              <span className="mt-1 block truncate font-mono text-xs text-muted-foreground">
                {instance.instanceKey}
              </span>
              <span className="mt-1 block text-xs capitalize text-muted-foreground">
                {instance.lifecycleStatus} · {new Date(instance.lastSeenAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">No sessions recorded.</p>
      )}
    </section>
  );
}

function SettingsTab(props: AgentMemberInspectorBodyProps) {
  return (
    <div className="space-y-6">
      <AgentMemberAvatarPicker
        member={props.member}
        disabled={props.saving}
        onSave={props.onUpdateAvatar}
      />
      {props.error ? (
        <p role="alert" className="text-sm text-destructive">
          {props.error}
        </p>
      ) : null}
      <section className="border-t border-border pt-5">
        <h3 className="text-sm font-medium">Name</h3>
        <p className="mt-1 text-xs text-muted-foreground">The stable Agent ID will not change.</p>
        {canRenameAgentMember(props.member.agentKind) ? (
          <div className="mt-3 flex gap-2">
            <Input
              value={props.name}
              onChange={(event) => props.onNameChange(event.target.value)}
              placeholder={props.member.displayName}
            />
            <Button
              variant="secondary"
              disabled={props.saving || !props.name.trim()}
              onClick={props.onRename}
            >
              Rename
            </Button>
          </div>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">
            {isProviderLockedAgentKind(props.member.agentKind)
              ? `${providerLockedDisplayName(props.member.agentKind)} uses the provider name and cannot be renamed.`
              : "This member cannot be renamed."}
          </p>
        )}
      </section>
      {props.member.lifecycleStatus !== "retired" ? (
        <section className="flex items-center justify-between gap-4 border-t border-border pt-5">
          <div>
            <h3 className="text-sm font-medium">Retire agent</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Stops new work while preserving history.
            </p>
          </div>
          <Button variant="destructive" onClick={props.onRetire}>
            Retire
          </Button>
        </section>
      ) : null}
    </div>
  );
}
